import type { Permission } from "./permissions";
import type { RelationshipResource, RelationshipUser } from "./relationships";
import { resolveAllRelationships, resolveRelationship } from "./relationships";
import { PERMISSION_RULES } from "./rules";

/**
 * Check if a user has a specific permission on a resource.
 */
export function can(
  user: RelationshipUser,
  permission: Permission,
  resource: RelationshipResource,
): boolean {
  const relationship = resolveRelationship(user, resource);
  if (!relationship) return false;

  const key = `${user.role}:${relationship}`;
  const allowed = PERMISSION_RULES[key];
  if (!allowed) return false;

  return allowed.includes("*" as Permission) || allowed.includes(permission);
}

/**
 * Check if a user has ANY of the specified permissions on a resource.
 */
export function canAny(
  user: RelationshipUser,
  permissions: Permission[],
  resource: RelationshipResource,
): boolean {
  return permissions.some((p) => can(user, p, resource));
}

/**
 * Check if a user has ALL of the specified permissions on a resource.
 */
export function canAll(
  user: RelationshipUser,
  permissions: Permission[],
  resource: RelationshipResource,
): boolean {
  return permissions.every((p) => can(user, p, resource));
}

/**
 * Check if a role has a permission in ANY relationship.
 * Used for coarse checks when no specific resource is available
 * (e.g., "should this sidebar link be visible at all?").
 */
export function roleHasPermission(
  role: string,
  permission: Permission,
): boolean {
  for (const [key, perms] of Object.entries(PERMISSION_RULES)) {
    if (!key.startsWith(`${role}:`)) continue;
    if (perms.includes("*" as Permission) || perms.includes(permission)) {
      return true;
    }
  }
  return false;
}

/**
 * Get all org IDs where a user has a specific permission.
 * Returns "all" if the user has unrestricted access for that permission.
 *
 * Used for building Prisma query filters.
 */
export function accessibleOrgsFor(
  user: RelationshipUser,
  permission: Permission,
): number[] | "all" {
  const relationships = resolveAllRelationships(user);
  const orgs: number[] = [];

  for (const { relationship, orgIds } of relationships) {
    const key = `${user.role}:${relationship}`;
    const allowed = PERMISSION_RULES[key];
    if (!allowed) continue;

    if (allowed.includes("*" as Permission) || allowed.includes(permission)) {
      if (orgIds === "all") return "all";
      orgs.push(...orgIds);
    }
  }

  return orgs;
}
